import { forwardRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import type { LibraryCard } from "@/lib/api/library-cards";
import { resolveLibraryCardFields, type CardRegistrationField } from "@/lib/libraryCardFields";
import {
  DEFAULT_LIBRARY_CARD_PRINT_SETTINGS,
  type LibraryCardPrintSettings,
} from "@/lib/libraryCardPrintConfig";

interface CardWithToken extends LibraryCard {
  verificationToken: string;
}

interface LibraryCardPrintSheetProps {
  cards: CardWithToken[];
  stampUrl?: string;
  signatureUrl?: string;
  printConfig?: LibraryCardPrintSettings;
  brandPrimaryColor?: string;
  libraryFullName?: string;
  libraryAddress?: string;
  fieldsByPatronType?: Record<string, CardRegistrationField[]>;
}

/** ISO/IEC 7810 ID-1 landscape (wallet card): 85.60 × 53.98 mm — same class as ATM / driver’s license. */
const CARD_W_MM = 85.6;
const CARD_H_MM = 53.98;
const CARD_GAP_MM = 4;
/** Portrait box: fixed square like professional ID samples — does not stretch to full card height. */
const PHOTO_BOX_MM = 18;

const DEFAULT_BRAND = "#2563EB";

function bannerLabelColor(backgroundHex: string): string {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(backgroundHex.trim());
  if (!m) return "#ffffff";
  const r = parseInt(m[1], 16) / 255;
  const g = parseInt(m[2], 16) / 255;
  const b = parseInt(m[3], 16) / 255;
  const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return luminance > 0.55 ? "#0f172a" : "#ffffff";
}

export const LibraryCardPrintSheet = forwardRef<HTMLDivElement, LibraryCardPrintSheetProps>(
  function LibraryCardPrintSheet(
    {
      cards,
      stampUrl,
      signatureUrl,
      printConfig = DEFAULT_LIBRARY_CARD_PRINT_SETTINGS,
      brandPrimaryColor = DEFAULT_BRAND,
      libraryFullName,
      libraryAddress,
      fieldsByPatronType = {},
    },
    ref
  ) {
    const cols = printConfig.cardsPerRow;
    const rows = printConfig.rowsPerPage;
    const perPage = cols * rows;
    const bandBg = /^#[0-9A-Fa-f]{6}$/.test(brandPrimaryColor) ? brandPrimaryColor : DEFAULT_BRAND;
    const bandFg = bannerLabelColor(bandBg);

    const pages: CardWithToken[][] = [];
    for (let i = 0; i < cards.length; i += perPage) {
      pages.push(cards.slice(i, i + perPage));
    }

    const verifyUrl = (token: string) =>
      typeof window !== "undefined"
        ? `${window.location.origin}/verify-card?t=${encodeURIComponent(token)}`
        : "";

    const formData = (card: CardWithToken): Record<string, unknown> => {
      const raw = card.user?.metadata as { formData?: Record<string, unknown> } | undefined;
      return raw?.formData && typeof raw.formData === "object"
        ? (raw.formData as Record<string, unknown>)
        : {};
    };

    return (
      <div ref={ref} className="library-card-print-root bg-white">
        <style>
          {`
          .library-card-print-root {
            color: #000000;
            font-family: Arial, Helvetica, sans-serif;
            -webkit-font-smoothing: auto;
          }
          @media print {
            @page { size: A4 portrait; margin: 10mm; }
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            .print\\:break-after { page-break-after: always; }
          }
        `}
        </style>
        {pages.map((pageCards, pageIndex) => (
          <div
            key={pageIndex}
            className="library-card-a4-page p-[10mm] w-[210mm] min-h-[297mm] box-border print:break-after bg-white flex justify-center"
          >
            <div
              style={{
                display: "grid",
                gridTemplateColumns: `repeat(${cols}, ${CARD_W_MM}mm)`,
                gridTemplateRows: `repeat(${rows}, ${CARD_H_MM}mm)`,
                gap: `${CARD_GAP_MM}mm`,
                alignContent: "start",
              }}
            >
              {pageCards.map((card) => {
                const ptName = card.membership?.patronType?.name?.trim() || "";
                const fd = formData(card);
                const regDate = card.membership?.joinedAt
                  ? new Date(card.membership.joinedAt).toLocaleDateString(undefined, {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })
                  : "";

                const headerTitle = (
                  libraryFullName?.trim() ||
                  card.tenant?.name ||
                  "Library"
                ).toUpperCase();
                const subtitle =
                  libraryAddress?.trim() || printConfig.institutionSubtitle?.trim() || "";
                const showHeaderName = printConfig.showLibraryFullName !== false;
                const showHeaderSub =
                  printConfig.showAddressOrSubtitle !== false && Boolean(subtitle?.trim());

                const prefix = (
                  printConfig.admissionBannerPrefix?.trim() || "ADMISSION TICKET"
                ).toUpperCase();
                const bannerText =
                  printConfig.showPatronTypeBanner && ptName ? `${prefix} — ${ptName}` : prefix;

                const patronLine =
                  [card.user?.lastName, card.user?.firstName]
                    .filter(Boolean)
                    .join(", ")
                    .toUpperCase() ||
                  card.user?.email ||
                  "Patron";

                const patronTypeId = card.membership?.patronType?.id ?? "";
                const patronFields =
                  fieldsByPatronType[patronTypeId] ?? fieldsByPatronType[""] ?? [];
                const registrationLines = resolveLibraryCardFields({
                  formData: fd,
                  patronFields,
                  printFields: printConfig.customFormFields,
                });

                return (
                  <div
                    key={card.id}
                    className="flex flex-col overflow-hidden bg-white"
                    style={{
                      width: `${CARD_W_MM}mm`,
                      height: `${CARD_H_MM}mm`,
                      fontFamily: "Arial, Helvetica, sans-serif",
                      color: "#000000",
                      border: "1px solid rgba(15, 23, 42, 0.16)",
                      boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                      borderRadius: "2.2mm",
                      borderTop: `2.5px solid ${bandBg}`,
                    }}
                  >
                    <div className="flex flex-shrink-0 flex-row items-stretch border-b border-slate-200/80 bg-white">
                      <div className="min-w-0 flex-1 px-1 py-0.5 text-left">
                        {showHeaderName ? (
                          <div
                            className="text-[6px] font-bold uppercase leading-tight tracking-wide"
                            style={{ lineHeight: 1.2, color: "#000000" }}
                          >
                            {headerTitle}
                          </div>
                        ) : null}
                        {showHeaderSub ? (
                          <div
                            className="mt-0.5 text-[5.5px] font-semibold leading-tight whitespace-pre-line"
                            style={{ lineHeight: 1.2, color: "#000000" }}
                          >
                            {subtitle}
                          </div>
                        ) : null}
                        {!showHeaderName && !showHeaderSub ? (
                          <div
                            className="text-[4.5px] leading-tight text-slate-400"
                            style={{ lineHeight: 1.2 }}
                          >
                            &nbsp;
                          </div>
                        ) : null}
                      </div>
                      <div
                        className="flex-shrink-0"
                        style={{
                          width: "8.5mm",
                          background: `linear-gradient(118deg, transparent 0%, ${bandBg}18 35%, ${bandBg}4d 100%)`,
                        }}
                        aria-hidden
                      />
                    </div>

                    {printConfig.showPatronTypeBanner ? (
                      <div
                        className="flex-shrink-0 px-1 py-0.5 text-center"
                        style={{ backgroundColor: bandBg, color: bandFg }}
                      >
                        <div className="text-[6px] font-bold uppercase tracking-wide leading-tight">
                          {bannerText}
                        </div>
                      </div>
                    ) : null}

                    <div className="flex-shrink-0 border-b border-slate-200/70 bg-white py-0.5 text-center">
                      <div
                        className="text-[6.5px] font-extrabold uppercase tracking-[0.18em]"
                        style={{ color: "#000000" }}
                      >
                        Library card
                      </div>
                    </div>

                    <div className="flex min-h-0 flex-1 flex-row items-start gap-1 bg-white px-1 py-0.5">
                      {printConfig.showProfilePhoto ? (
                        <div
                          className="flex-shrink-0 self-start overflow-hidden rounded-[1mm] bg-slate-100 ring-1 ring-slate-300/80"
                          style={{
                            width: `${PHOTO_BOX_MM}mm`,
                            height: `${PHOTO_BOX_MM}mm`,
                          }}
                        >
                          {card.user?.profilePicture ? (
                            <img
                              src={card.user.profilePicture}
                              alt=""
                              className="h-full w-full object-cover object-top"
                            />
                          ) : (
                            <div className="flex h-full w-full items-center justify-center text-[9px] font-light text-slate-400">
                              Photo
                            </div>
                          )}
                        </div>
                      ) : null}

                      <div
                        className="min-w-0 flex-1 leading-snug"
                        style={{
                          color: "#000000",
                          fontSize: "6px",
                          fontWeight: 600,
                          lineHeight: 1.2,
                        }}
                      >
                        <div className="mb-0.5 break-words">
                          <span className="font-bold uppercase tracking-wide">Name:</span>{" "}
                          <span className="font-bold uppercase">{patronLine}</span>
                        </div>
                        {printConfig.showCardNumber ? (
                          <div className="mb-0.5 break-words">
                            <span className="font-bold uppercase tracking-wide">ID no.:</span>{" "}
                            <span className="font-mono font-bold">{card.virtualCardNumber}</span>
                          </div>
                        ) : null}
                        {printConfig.showEmail && card.user?.email ? (
                          <div className="mb-0.5 break-words">
                            <span className="font-bold uppercase">Email:</span>{" "}
                            <span>{card.user.email}</span>
                          </div>
                        ) : null}
                        {printConfig.showPhone && card.user?.phone ? (
                          <div className="mb-0.5 break-words">
                            <span className="font-bold uppercase">Phone:</span>{" "}
                            <span>{card.user.phone}</span>
                          </div>
                        ) : null}
                        {printConfig.showRegistrationDate && regDate ? (
                          <div className="mb-0.5 break-words">
                            <span className="font-bold uppercase">Registered:</span>{" "}
                            <span>{regDate}</span>
                          </div>
                        ) : null}
                        {registrationLines.map((line) => (
                          <div key={line.key} className="mb-0.5 break-words">
                            <span className="font-bold uppercase">{line.label}:</span>{" "}
                            <span>{line.value}</span>
                          </div>
                        ))}
                        {printConfig.showSessionLine ? (
                          <div
                            className="mt-0.5 border-t border-dotted border-slate-300/90 pt-0.5"
                            style={{ color: "#000000" }}
                          >
                            <span className="font-bold uppercase">
                              {printConfig.sessionLineLabel}:
                            </span>
                            <span className="ml-0.5 inline-block min-w-[20mm] border-b border-slate-500 align-bottom">
                              &nbsp;
                            </span>
                          </div>
                        ) : null}
                      </div>

                      <div className="flex w-[15.5mm] flex-shrink-0 flex-col items-center gap-0.5 border-l border-slate-200/80 pl-0.5">
                        <QRCodeSVG
                          value={verifyUrl(card.verificationToken)}
                          size={36}
                          level="M"
                          includeMargin={false}
                        />
                        {stampUrl && printConfig.showStampImage !== false ? (
                          <img
                            src={stampUrl}
                            alt=""
                            className="max-h-[8mm] w-auto max-w-[14mm] object-contain"
                          />
                        ) : null}
                        {signatureUrl && printConfig.showSignatureImage !== false ? (
                          <img
                            src={signatureUrl}
                            alt=""
                            className="max-h-[5mm] w-auto max-w-[14mm] object-contain"
                          />
                        ) : null}
                        {printConfig.showLibrarianSigLine ? (
                          <div
                            className="text-center text-[5px] font-semibold leading-tight"
                            style={{ color: "#000000" }}
                          >
                            {printConfig.librarianSigLabel}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  }
);
